import { beginDetachedChatTransfer } from "./detached-chat-transfer";
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import type { CursorInputContextValue } from "@/components/layout/cursor-input-context";
import { ConversationFrame } from "@/components/shapes/conversation-frame";
import {
  isMessageVisibleInConversationFrame,
  metadataString,
} from "@/components/shapes/conversation-frame/conversation-frame-utils";
import { itemContextMenu } from "@/components/focus/item-context-menu";
import { ItemMenuButton } from "@/components/focus/item-menu";
import { FrameHoverChrome } from "@/components/shapes/shared/frame-hover-chrome";
import {
  FrameRendererContext,
  plainFrameRenderer,
  type FrameItemMenuProps,
  type FrameRenderer,
} from "@/components/shapes/shared/frame-renderer-context";
import { FrameContextMenu } from "@/components/shapes/shared/frame-context-menu";
import { useFrameHoverActivation } from "@/components/shapes/shared/use-frame-hover-activation";
import { useDetachedChatWindow } from "./use-detached-chat-window";
import { useWorkspaceConversationDismiss } from "./use-workspace-conversation-dismiss";
import { ChatWindowSnapPreview } from "./chat-window-snap-preview";
import { useConversationStore } from "@/stores/conversation-store";
import { useProjectStore } from "@/stores/project-store";
import {
  useWorkspaceConversationOverlay,
  type WorkspaceConversationOverlay,
} from "@/stores/workspace-conversation-overlay-store";
import { requestNativeGlassRefresh } from "./use-native-glass-surfaces";
import type { FrameData } from "@/lib/frame-data";
import { useWallVisibleHere } from "@/lib/wall-visibility";
import { useT } from "@/i18n";
import { compactMessages } from "@/i18n/messages/compact";

const EDGE_INSET = 16;
const DEFAULT_SIZE = { width: 440, height: 700 };
const DEFAULT_POSITION = { right: 24, top: 48 };
const MIN_SIZE = { width: 320, height: 360 };
const NATIVE_SURFACE_ID = "workspace-conversation-overlay";

function refreshWorkspaceConversationGlass() {
  requestNativeGlassRefresh({ trackTransition: false });
}

function frameDataFromOverlay({
  frame,
}: WorkspaceConversationOverlay): FrameData {
  return {
    frameId: frame.id,
    spaceId: frame.project ?? frame.space,
    frameType: frame.frame_type,
    title: frame.title,
    content: frame.content,
    status: frame.status,
    agentId: frame.agent_id,
    parentFrame: frame.parent_frame ?? null,
    metadata: frame.metadata,
    createdAt: frame.created_at,
    taskTicket:
      frame.task_id && frame.task_ticket_number != null
        ? {
            id: frame.task_id,
            number: frame.task_ticket_number,
            title: frame.task_title ?? "",
          }
        : null,
    editedByUserAt: frame.edited_by_user_at ?? null,
  };
}

/** The docked chat's ⋯ (opened-window rule 9): the item menu, no Focus. */
function renderDockedChatMenu(props: FrameItemMenuProps) {
  return <ItemMenuButton {...props} />;
}

/** The docked chat's surface: its right-click is its ⋯ menu. */
const dockedChatFrameRenderer: FrameRenderer = {
  ...plainFrameRenderer,
  contextMenu: itemContextMenu,
};

export function WorkspaceConversationOverlaySurface({
  inputContext,
}: {
  inputContext: CursorInputContextValue;
}) {
  const t = useT(compactMessages);
  const overlay = useWorkspaceConversationOverlay((state) => state.overlay);
  const close = useWorkspaceConversationOverlay((state) => state.close);
  const currentProjectId = useProjectStore((state) => state.currentProject?.id);
  const currentSpaceId = useProjectStore((state) => state.currentSpace?.id);
  const messages = useConversationStore((state) => state.messages);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const hover = useFrameHoverActivation(surfaceRef);
  const open = overlay !== null;
  const wallVisible = useWallVisibleHere();
  const dismiss = useWorkspaceConversationDismiss({ open, surfaceRef, close });
  const overlayFrameId = overlay?.frame.id ?? null;
  const defaultSize = useMemo(
    () =>
      overlayFrameId
        ? {
            width: Math.min(
              DEFAULT_SIZE.width,
              Math.max(
                1,
                window.innerWidth - DEFAULT_POSITION.right - EDGE_INSET,
              ),
            ),
            height: Math.min(
              DEFAULT_SIZE.height,
              Math.max(
                1,
                window.innerHeight - DEFAULT_POSITION.top - EDGE_INSET,
              ),
            ),
          }
        : DEFAULT_SIZE,
    [overlayFrameId],
  );
  const {
    position,
    dragHandleProps,
    snapPreview,
    size,
    style: sizeStyle,
    resizeHandleProps,
  } = useDetachedChatWindow({
    open,
    resetKey: overlayFrameId,
    surfaceRef,
    defaultSize,
    minSize: MIN_SIZE,
    viewportInset: EDGE_INSET,
    onDragStart: (event, box) => {
      if (!overlay) return;
      beginDetachedChatTransfer({
        data: frameDataFromOverlay(overlay),
        messages: frameMessages,
        projectId: overlay.project.id,
        spaceId: overlay.space.id,
        box,
        event,
        onTransfer: close,
      });
    },
  });

  useLayoutEffect(() => {
    if (!overlayFrameId) return;
    refreshWorkspaceConversationGlass();
  }, [overlayFrameId, position?.x, position?.y, size.height, size.width]);

  useLayoutEffect(() => {
    if (!open) return;
    return refreshWorkspaceConversationGlass;
  }, [open]);

  const frameData = useMemo(
    () => (overlay ? frameDataFromOverlay(overlay) : null),
    [overlay],
  );
  const frameMessages = useMemo(() => {
    if (!overlay || !frameData) return [];
    const metadata = frameData.metadata;
    return messages.filter((message) =>
      isMessageVisibleInConversationFrame(message, {
        conversationId: metadataString(
          metadata,
          "conversationId",
          "conversation_id",
        ),
        id: overlay.frame.id,
        requireExactIdentity:
          metadata.conversationRequiresExactIdentity === true,
        executionId: metadataString(
          metadata,
          "executionId",
          "execution_id",
          "liveExecutionId",
        ),
        runtimeSessionId: metadataString(
          metadata,
          "runtimeSessionId",
          "liveSessionId",
        ),
      }),
    );
  }, [frameData, messages, overlay]);

  useEffect(() => {
    if (!overlay) return;
    const scopeMatches =
      overlay.project.id === currentProjectId &&
      overlay.space.id === currentSpaceId;
    if (!scopeMatches) close();
  }, [close, currentProjectId, currentSpaceId, overlay]);

  // Once per opened chat: its own updates must not pull the keyboard back.
  useEffect(() => {
    if (!overlayFrameId) return;
    const frame = requestAnimationFrame(() => {
      surfaceRef.current
        ?.querySelector<HTMLElement>("textarea, input")
        ?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [overlayFrameId]);

  if (!overlay || !frameData) return null;

  return (
    <>
      {wallVisible && (
        <div
          // Over the Workspace a click outside closes the chat; over other
          // apps no backdrop, so clicks keep passing to them.
          role="presentation"
          data-interactive="true"
          data-testid="workspace-conversation-backdrop"
          className="pointer-events-auto fixed inset-0 z-[60]"
          {...dismiss.backdropProps}
        />
      )}
      <ChatWindowSnapPreview preview={snapPreview} />
      <FrameRendererContext.Provider value={dockedChatFrameRenderer}>
        <FrameContextMenu frameId={overlay.frame.id} fallbackFrame={frameData}>
          <div
            ref={surfaceRef}
            data-interactive="true"
            // ⌘K typed in the chat opens the hub, not the wall's search.
            data-own-shortcuts="true"
            data-canvas-frame-id={overlay.frame.id}
            data-frame-type="conversation"
            data-frame-bottom-active={hover.bottomActive ? "true" : "false"}
            data-frame-top-active={hover.topActive ? "true" : "false"}
            data-testid="workspace-conversation-overlay"
            className="pointer-events-auto fixed z-[60] overflow-visible rounded-frame"
            style={{
              ...sizeStyle,
              left: position?.x,
              right: position ? undefined : DEFAULT_POSITION.right,
              top: position?.y ?? DEFAULT_POSITION.top,
              transform: "none",
            }}
            {...dragHandleProps}
            {...dismiss.surfaceProps}
            onPointerMove={(event) => {
              dragHandleProps.onPointerMove(event);
              hover.handlePointerMove(event);
            }}
            onPointerLeave={hover.handlePointerLeave}
          >
            <div className="h-full w-full overflow-hidden rounded-frame">
              <ConversationFrame
                id={overlay.frame.id}
                data={frameData}
                selected={false}
                detached={{
                  actionFrameId: overlay.frame.id,
                  inputContext,
                  messages: frameMessages,
                  nativeSurfaceId: NATIVE_SURFACE_ID,
                  onClose: close,
                  submitFrameId: overlay.frame.id,
                }}
              />
            </div>
            <FrameHoverChrome
              frameId={overlay.frame.id}
              actionFrameId={overlay.frame.id}
              frame={frameData}
              frameRootRef={surfaceRef}
              bottomActive={hover.bottomActive}
              topActive={hover.topActive}
              onBottomActiveChange={hover.updateBottomActive}
              onTopActiveChange={hover.updateTopActive}
              snoozeBottom={hover.snoozeBottom}
              inputContext={inputContext}
              onCloseFrame={close}
              onConversationArtifactPresented={close}
              renderItemMenu={renderDockedChatMenu}
            />
            <button
              type="button"
              aria-label={t("resizeChat")}
              data-no-drag
              data-resize-position="bottom-right"
              data-testid="workspace-conversation-overlay-resize"
              className="denker-detached-resize-target denker-frame-resize-indicator absolute bottom-0 right-0 z-[70] h-11 w-11 touch-none select-none cursor-se-resize rounded-br-frame bg-transparent"
              {...resizeHandleProps}
            />
          </div>
        </FrameContextMenu>
      </FrameRendererContext.Provider>
    </>
  );
}
